import { createFileRoute, Link } from "@tanstack/react-router";
import { NoteListItem } from "@/components/note-list-item";
import { WeekView, type Note as WeekViewNote } from "@/components/week-view";
import { CompactNoteForm } from "@/components/compact-note-form";
import { useQuery } from "@tanstack/react-query";
import { notesQueryOptions } from "@/queries/notes";
import { Button } from "@/components/ui/button";
import { Calendar } from "lucide-react";
import { useMemo, useState } from "react";
import { useDocumentTitle } from "usehooks-ts";
import { motion, AnimatePresence } from "framer-motion";
import { useNoteCreation } from "@/hooks/useNoteCreation";
import { PageHeader } from "@/components/layout/page-header";
import { UnauthenticatedPage } from "@/components/layout/unauthenticated-page";

export const Route = createFileRoute("/upcoming")({
  component: UpcomingView,
});

function UpcomingView() {
  const { user } = Route.useRouteContext();
  useDocumentTitle("Upcoming | Notebook");
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [quickAddDate, setQuickAddDate] = useState<Date | undefined>();
  const [formExpanded, setFormExpanded] = useState(false);

  const { data: allNotes = [] } = useQuery({
    ...notesQueryOptions(user?.id),
    enabled: !!user?.id,
  });

  const { handleNoteCreated, isPending: isCreateNotePending } = useNoteCreation(
    user?.id ?? "",
  );

  const scheduledNotes = useMemo(() => {
    return allNotes.filter((note: any) => note.dueDate && !note.completed);
  }, [allNotes]);

  const selectedDayNotes = useMemo(() => {
    if (!selectedDate) return [];

    const selectedStart = new Date(selectedDate);
    selectedStart.setHours(0, 0, 0, 0);
    const selectedEnd = new Date(selectedStart);
    selectedEnd.setDate(selectedEnd.getDate() + 1);

    return scheduledNotes.filter((note: any) => {
      const dueDate = new Date(note.dueDate);
      return dueDate >= selectedStart && dueDate < selectedEnd;
    });
  }, [scheduledNotes, selectedDate]);

  const handleDayClick = (date: Date) => {
    if (selectedDate && isSameDay(selectedDate, date)) {
      setSelectedDate(null);
    } else {
      setSelectedDate(date);
    }
  };

  const handleQuickAdd = (date: Date) => {
    setQuickAddDate(date);
    setFormExpanded(true);
  };

  const handleFormExpandChange = (expanded: boolean) => {
    setFormExpanded(expanded);
    if (!expanded) {
      setQuickAddDate(undefined);
    }
  };

  if (!user?.id) {
    return (
      <UnauthenticatedPage
        title="Upcoming"
        description="Sign in to see your scheduled tasks"
        icon={Calendar}
        iconColor="text-blue-500"
      />
    );
  }

  return (
    <>
      <PageHeader
        title="Upcoming"
        icon={Calendar}
        iconColor="text-blue-500"
        actions={
          <Button
            variant="outline"
            size="sm"
            asChild
            className="text-xs md:text-sm"
          >
            <Link to="/today">Today</Link>
          </Button>
        }
      />

      <>
        <div className="animate-fade-in max-w-4xl mx-auto w-full mb-6 md:mb-8 py-3 md:py-4 px-4">
          <CompactNoteForm
            onSubmit={handleNoteCreated}
            isSubmitting={isCreateNotePending}
            defaultDate={quickAddDate}
            autoExpand={formExpanded}
            onExpandChange={handleFormExpandChange}
          />
        </div>

        <div className="flex-1 space-y-6 md:space-y-8 px-4 md:px-6 w-full max-w-4xl mx-auto min-h-screen overflow-x-hidden">
          <div className="bg-card border rounded-lg p-4">
            <WeekView
              notes={scheduledNotes as WeekViewNote[]}
              onDayClick={handleDayClick}
              onAddNote={handleQuickAdd}
            />
          </div>

          {selectedDate && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-4"
            >
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold">
                  {formatFullDate(selectedDate)}
                </h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedDate(null)}
                >
                  Clear
                </Button>
              </div>

              {selectedDayNotes.length > 0 ? (
                <ul className="space-y-2">
                  <AnimatePresence mode="popLayout" initial={false}>
                    {selectedDayNotes.map((note: any) => (
                      <NoteListItem key={note.id} note={note} />
                    ))}
                  </AnimatePresence>
                </ul>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <p className="text-sm">No tasks scheduled for this day</p>
                </div>
              )}
            </motion.div>
          )}

          {!selectedDate && scheduledNotes.length === 0 && (
            <div className="text-center py-12">
              <div className="space-y-3">
                <Calendar className="mx-auto h-12 w-12 text-blue-500" />
                <div className="space-y-1">
                  <h3 className="text-xl font-display font-semibold">
                    No upcoming tasks
                  </h3>
                  <p className="text-muted-foreground">
                    Create a task above and add a due date to see it here.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </>
    </>
  );
}

function isSameDay(date1: Date, date2: Date): boolean {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

function formatFullDate(date: Date): string {
  return date.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}
